import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  FileText, Download, Eye, Search, Filter, FolderOpen, Clock,
  Plus, Send, CheckCircle, AlertTriangle, RefreshCw, X,
  ChevronRight, FileCheck, Inbox, Bell
} from 'lucide-react';
import { format } from 'date-fns';

const ViewerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('documents');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestForm, setRequestForm] = useState({
    request_type: 'new_document',
    title: '',
    description: '',
    department: '',
    urgency: 'normal'
  });

  // Determine if user is a manager who needs approval dashboard
  const isManager = user?.role === 'manager' || user?.role === 'admin' || user?.role === 'qa_manager' || user?.is_department_manager;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [docsRes, requestsRes] = await Promise.all([
        apiClient.get('/documents/viewer-access'),
        apiClient.get('/documents/requests')
      ]);
      setDocuments(docsRes.data || []);
      setMyRequests(requestsRes.data || []);

      // Fetch pending approvals for managers
      if (isManager) {
        const approvalsRes = await apiClient.get('/documents/my-approvals');
        setPendingApprovals(approvalsRes.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [isManager]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDownload = async (doc) => {
    try {
      const response = await apiClient.get(`/documents/${doc.id}/download`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename || doc.document_name || 'document';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Document downloaded');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download document');
    }
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    if (!requestForm.title.trim()) {
      toast.error('Please enter a title for your request');
      return;
    }
    try {
      await apiClient.post('/documents/request', requestForm);
      toast.success('Document request submitted successfully');
      setShowRequestModal(false);
      setRequestForm({
        request_type: 'new_document',
        title: '',
        description: '',
        department: '',
        urgency: 'normal'
      });
      fetchData();
    } catch (error) {
      console.error('Submit request error:', error);
      toast.error('Failed to submit request');
    }
  };

  const handleApproveDocument = async (doc, action) => {
    try {
      if (action === 'dept_review') {
        await apiClient.post(`/documents/${doc.id}/dept-approve`, {
          comments: 'Approved by department manager',
          signature_data: user?.name || 'Manager',
          signature_type: 'typed'
        });
        toast.success('Document approved and forwarded to QA Manager');
      } else {
        await apiClient.post(`/documents/${doc.id}/approve`, {
          comments: 'Final approval',
          signature_data: user?.name || 'QA Manager',
          signature_type: 'typed'
        });
        toast.success('Document approved');
      }
      fetchData();
    } catch (error) {
      console.error('Approval error:', error);
      toast.error('Failed to approve document');
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = !searchTerm || 
      doc.document_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || doc.document_category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = [...new Set(documents.map(d => d.document_category).filter(Boolean))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="viewer-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Document Portal</h1>
          <p className="text-gray-600">Access approved documents and submit requests</p>
        </div>
        <button
          onClick={() => setShowRequestModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          data-testid="new-request-btn"
        >
          <Plus className="h-4 w-4" />
          New Document Request
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{documents.length}</p>
              <p className="text-sm text-gray-500">Available Documents</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {myRequests.filter(r => r.status === 'pending').length}
              </p>
              <p className="text-sm text-gray-500">Pending Requests</p>
            </div>
          </div>
        </div>
        {isManager && (
          <>
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Inbox className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{pendingApprovals.length}</p>
                  <p className="text-sm text-gray-500">Needs Your Approval</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {myRequests.filter(r => r.status === 'completed').length}
                  </p>
                  <p className="text-sm text-gray-500">Completed Requests</p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Manager Approval Section */}
      {isManager && pendingApprovals.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Bell className="h-6 w-6 text-yellow-600" />
            <h2 className="text-lg font-semibold text-yellow-800">Documents Awaiting Your Approval</h2>
            <span className="px-2 py-1 bg-yellow-200 text-yellow-800 rounded-full text-sm font-medium">
              {pendingApprovals.length}
            </span>
          </div>
          <div className="space-y-3">
            {pendingApprovals.map((doc) => (
              <div key={doc.id} className="bg-white rounded-lg p-4 border border-yellow-100 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <FileCheck className="h-8 w-8 text-yellow-600" />
                  <div>
                    <p className="font-medium text-gray-900">{doc.document_name}</p>
                    <p className="text-sm text-gray-500">
                      {doc.document_type} • {doc.department || doc.bms_department} • 
                      Submitted: {doc.submitted_for_approval_date ? format(new Date(doc.submitted_for_approval_date), 'MMM dd, yyyy') : 'N/A'}
                    </p>
                    <p className="text-sm text-gray-500">
                      Submitted by: {doc.submitter_name || 'Unknown'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    doc.approval_action_needed === 'dept_review' 
                      ? 'bg-purple-100 text-purple-700' 
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {doc.approval_action_needed === 'dept_review' ? 'Dept Review' : 'Final Approval'}
                  </span>
                  <button
                    onClick={() => handleApproveDocument(doc, doc.approval_action_needed)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => navigate(`/documents?doc=${doc.id}`)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
                  >
                    Review
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('documents')}
            className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'documents'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              BMS Documents
            </div>
          </button>
          <button
            onClick={() => setActiveTab('requests')}
            className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'requests'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <Send className="h-4 w-4" />
              My Requests
              {myRequests.filter(r => r.status === 'pending').length > 0 && (
                <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs">
                  {myRequests.filter(r => r.status === 'pending').length}
                </span>
              )}
            </div>
          </button>
        </nav>
      </div>

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div className="space-y-4">
          {/* Search and Filter */}
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Documents Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredDocuments.map((doc) => (
              <div key={doc.id} className="bg-white rounded-xl shadow-sm border p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <FileText className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 line-clamp-1">{doc.document_name}</p>
                      <p className="text-xs text-gray-500">{doc.document_type}</p>
                    </div>
                  </div>
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                    v{doc.version || '1.0'}
                  </span>
                </div>
                
                {doc.description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{doc.description}</p>
                )}
                
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                  <FolderOpen className="h-3 w-3" />
                  <span>{doc.document_category || 'General'}</span>
                  <span className="mx-1">•</span>
                  <span>{doc.department || doc.bms_department}</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleDownload(doc)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  >
                    <Download className="h-4 w-4" />
                    Download
                  </button>
                  <button
                    onClick={() => navigate(`/bms-admin?preview=${doc.id}`)}
                    className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    title="Preview"
                  >
                    <Eye className="h-4 w-4 text-gray-600" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredDocuments.length === 0 && (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No documents found</p>
              <p className="text-sm text-gray-400">Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      )}

      {/* Requests Tab */}
      {activeTab === 'requests' && (
        <div className="space-y-4">
          {myRequests.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <Send className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No document requests yet</p>
              <button
                onClick={() => setShowRequestModal(true)}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Submit Your First Request
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Request</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Urgency</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {myRequests.map((req) => (
                    <tr key={req.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{req.title}</p>
                        <p className="text-sm text-gray-500 line-clamp-1">{req.description}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {req.request_type?.replace('_', ' ')}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          req.urgency === 'critical' ? 'bg-red-100 text-red-700' :
                          req.urgency === 'high' ? 'bg-orange-100 text-orange-700' :
                          req.urgency === 'normal' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {req.urgency}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          req.status === 'completed' ? 'bg-green-100 text-green-700' :
                          req.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                          req.status === 'rejected' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {req.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {req.created_at ? format(new Date(req.created_at), 'MMM dd, yyyy') : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* New Request Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">New Document Request</h2>
              <button onClick={() => setShowRequestModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmitRequest} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Request Type</label>
                <select
                  value={requestForm.request_type}
                  onChange={(e) => setRequestForm({...requestForm, request_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="new_document">New Document</option>
                  <option value="revision">Document Revision</option>
                  <option value="access">Access Request</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={requestForm.title}
                  onChange={(e) => setRequestForm({...requestForm, title: e.target.value})}
                  placeholder="Brief title for your request"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={requestForm.description}
                  onChange={(e) => setRequestForm({...requestForm, description: e.target.value})}
                  placeholder="Describe what document you need or the changes required..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                  <select
                    value={requestForm.department}
                    onChange={(e) => setRequestForm({...requestForm, department: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Department</option>
                    <option value="0100">Sales and Estimating</option>
                    <option value="0200">Project Administration</option>
                    <option value="0300">Supply Chain</option>
                    <option value="0400">Engineering</option>
                    <option value="0500">Manufacturing</option>
                    <option value="0600">Quality Control</option>
                    <option value="0700">Aftermarket</option>
                    <option value="0800">Accounts</option>
                    <option value="0900">BMS Administration</option>
                    <option value="1000">Human Resources</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Urgency</label>
                  <select
                    value={requestForm.urgency}
                    onChange={(e) => setRequestForm({...requestForm, urgency: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowRequestModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewerDashboard;
